// mailListActions.ts — Cluster POSTFACH (G1/G2/G3)
//
// Die drei Kontextmenü-Abläufe der Mailliste, aus MailView herausgelöst und ohne
// JSX: „Vorgang erstellen", „Einem Vorgang zuweisen…" und „Kontakt zuweisen…".
//
// Sie sind bewusst KEINE Zweitfassung der Abläufe aus dem Mail-Detailfenster,
// sondern rufen dieselben Fenster und dieselben Hilfsfunktionen auf:
//   - Erkennung  → `resolveMailRecognition` (serverseitig, Cluster ERKENNUNG A1)
//   - Anhänge    → `buildMailAttachments` (über die ganze Konversation)
//   - Zuweisung  → `openMailContactAssign` + `schliesseZuweisungAb`
//   - Vorgänge   → `openMailVorgangPicker` + `assignMailToZiele`
//   - Anlegen    → `openVorgangCreateFlow` mit `mailVorgangPrefill`
// Ändert sich einer dieser Bausteine, ändert sich der Rechtsklick-Weg mit.
//
// Nach jeder erfolgreichen Zuweisung wird archiviert (Entscheidung E03) — auch
// dann, wenn nur die MAIL einem Kontakt zugewiesen wurde und kein Vorgang
// entstanden ist. Und immer in der tragenden Reihenfolge: ERST verknüpfen, DANN
// archivieren. Der Archiv-Move vergibt eine neue Message-Id; ein danach
// geschriebener Link zeigte auf die tote Id.

import { graphMailService, type Email } from "../../services/graphMailService";
import { mailLinkService, type CreateMailLinkInput } from "../../services/mailLinkService";
import { extractMailServerCode, MAIL_CAPTURE_PENDING_MESSAGE, reportMailError } from "../../services/mailError";
import type { OpenWindowConfig } from "../windowManager/WindowManagerContext";
import {
    openVorgangCreateFlow,
    type VorgangCreateResult,
} from "../vorgang/create/VorgangCreateFlowWindow";
import { openMailContactAssign } from "./MailContactAssignWindow";
import { openMailVorgangPicker } from "./MailVorgangPickerWindow";
import { vorgangStartAus, type MailZuweisungErgebnis } from "./mailContactAssign";
import type { VorgangFuerVorgabe } from "../vorgang/create/vorgangFuer";
import { schliesseZuweisungAb } from "./mailZuweisungAbschluss";
import { assignMailToZiele } from "./mailVorgangAssign";
import { bindeVerlaufAnZiel } from "./mailVerlaufBinden";
import { mailVorgangPrefill } from "./mailVorgangPrefill";
import { buildMailAttachments, type MailListAttachments } from "./mailListAttachments";
import { bilderAlsFlowAnhaenge, frageBilderAuswahl, uebernimmBilderInZiele } from "./mailBilderUebernahme";
import { bildDateiname } from "./inlineBilder";
import { resolveMailRecognition, vorgangsKontaktAusErkennung, type MailRecognition } from "./mailListRecognition";
import { openSchadenAblage, type SchadenAblageModus } from "./SchadenAblageFenster";

/** Alles, was ein Kontextmenü-Ablauf von der Mailansicht braucht. */
export type MailListActionCtx = {
    email: Email;
    mailbox: string;
    openWindow: (config: OpenWindowConfig) => void;
    /** Zeile aus der Liste nehmen (nach dem Archivieren). */
    onDone: (messageId: string) => void;
    /** Ordnerbaum/Zähler des Postfachs auffrischen — sonst bleibt das Badge stehen. */
    onRefreshFolders: (mailbox: string) => void;
    /** Fehler, die den Ablauf beenden. */
    onError: (msg: string) => void;
    /** Neutrale Hinweise (degradierte Erkennung, fehlende Anhänge) — kein Fehler. */
    onNote: (msg: string) => void;
    /** Wechselt nach eindeutiger Zuordnung in die Vorgangs-Detailroute. */
    navigateToVorgang: (vorgangId: number) => void;
    /**
     * Schadenpostfach: nach der Ablage in den Schaden-Ordner der Kundenakte
     * wechseln (Kontakt → „Dokumente" → Ordner), wie nach dem Zuweisen an
     * einen Vorgang in dessen Detailroute.
     */
    navigateToSchadenOrdner?: (contactId: number, ordner: string) => void;
    /**
     * Liegt eine Nachricht im Posteingang ihres Postfachs? Entscheidet beim
     * Zuweisen des Verlaufs, welche seiner Nachrichten mit archiviert werden
     * dürfen. Fehlt die Auskunft, wird nur verlinkt — nicht verschoben.
     */
    istImPosteingang?: (email: Email) => boolean;
    /** T-29 — Einzelansicht: nach dem Zuordnen nennen, wie viele Mails mitgewandert sind. */
    mitgenommeneNennen?: boolean;
    /**
     * Schadenpostfach (schaden@): „Vorgang erstellen/zuweisen" legt die Mail in
     * den Schaden-Ordner der Kundenakte, statt sie an einen Vorgang zu hängen.
     */
    schadenAblage?: boolean;
};

/**
 * Schnappschuss der Mail für den MailLink — identische Felder wie
 * `MailDetailWindow.mailSnapshot`. Er wird VOR dem Archivieren gezogen, weil die
 * Zeile danach aus der Liste verschwindet.
 */
export function buildMailSnapshot(email: Email, mailbox: string): CreateMailLinkInput {
    return {
        mailbox,
        messageId: email.id,
        conversationId: email.conversationId ?? null,
        // Ohne sie entdoppelt der Server zwei Kopien DERSELBEN Mail nicht:
        // `messageId` ist die Graph-`id` und je Ordner verschieden.
        internetMessageId: email.internetMessageId ?? null,
        subject: email.subject ?? null,
        senderName: email.senderName ?? null,
        senderEmail: email.senderEmail ?? null,
        receivedAt:
            email.date instanceof Date && !Number.isNaN(email.date.getTime())
                ? email.date.toISOString()
                : null,
        preview: email.preview ?? null,
    };
}

/**
 * Archivieren + Zeile entfernen + Ordnerbäume auffrischen.
 *
 * Rückgabe `false` = das Archivieren ist gescheitert (typischerweise fehlendes
 * Postfach-Recht). Der Fehler wird benannt, nicht verschluckt: sonst gälte die
 * Aktion als erledigt, während die Mail unverändert im Postfach liegt.
 */
async function archiviereUndEntferne(ctx: MailListActionCtx): Promise<boolean> {
    try {
        await graphMailService.archiveMessage(ctx.mailbox, ctx.email.id, {
            // T-240: die Zuweisung hat die Sicherung gerade erst angestoßen.
            onWarten: () => ctx.onNote("Die Mail wird gesichert und danach archiviert …"),
        });
    } catch (err: unknown) {
        ctx.onError(reportMailError({
            area: "mail",
            action: "archive",
            error: err,
            fallbackMessage: "Die Mail konnte nicht archiviert werden.",
        }));
        return false;
    }
    ctx.onDone(ctx.email.id);
    ctx.onRefreshFolders(ctx.mailbox);
    return true;
}

/** Datum der Mail für sprechende Bildnamen („Bild aus Mail 2026-10-07.png"). */
function mailDatum(ctx: MailListActionCtx): Date {
    const d: Date | string | undefined = ctx.email.date;
    return d instanceof Date ? d : new Date(d ?? Date.now());
}

/** Den Erstell-Flow mit Kontakt und Anhängen öffnen — wie aus der geöffneten Mail. */
function oeffneVorgangFlow(
    ctx: MailListActionCtx,
    kontakt: { id: number; displayName?: string } | null,
    anhaenge: Pick<MailListAttachments, "items" | "bilder">,
    vorgangFuer?: VorgangFuerVorgabe,
): void {
    const snap = buildMailSnapshot(ctx.email, ctx.mailbox);
    // T-236: Bilder aus dem Mailtext als eigene, nicht vorausgewählte Gruppe
    // hinter den Dateien — wie aus der geöffneten Mail.
    const bilder = bilderAlsFlowAnhaenge(anhaenge.bilder ?? [], mailDatum(ctx));
    openVorgangCreateFlow(ctx.openWindow, {
        // E1/E5: einschrittig, und der Mail-Betreff reist als Angebot mit (C3:
        // vorbelegt wird er nicht).
        ...mailVorgangPrefill(kontakt, ctx.email.subject ?? null),
        // PO 01.10.2026: die in „Kontakt zuweisen" getroffene Wahl Person/Firma.
        vorgangFuer,
        // PO 14.09.2026: ohne Kunden darf aus der Mail auch ein PERSÖNLICHER
        // Vorgang werden (siehe MailDetailWindow). Mit erkanntem Kunden bleibt
        // es beim Kontakt-Vorgang — sonst verschwände der Schriftverkehr für
        // alle anderen.
        allowPersoenlich: kontakt == null,
        mailAttachments:
            anhaenge.items.length + bilder.length > 0
                ? {
                      mailbox: ctx.mailbox,
                      items: [
                          ...anhaenge.items.map((a) => ({
                              messageId: a.messageId,
                              attachmentId: a.id,
                              name: a.name,
                              size: a.size,
                          })),
                          ...bilder,
                      ],
                  }
                : undefined,
        onCreated: async (result: VorgangCreateResult) => {
            try {
                if (result.vorgangId) {
                    await mailLinkService.create({ ...snap, vorgangId: result.vorgangId });
                } else if (result.todoId) {
                    await mailLinkService.create({ ...snap, todoId: result.todoId });
                }
            } catch (e) {
                ctx.onError(reportMailError({
                    area: "mail",
                    action: "link",
                    error: e,
                    fallbackMessage: "Der Vorgang wurde erstellt, die Mail konnte aber nicht verknüpft werden.",
                }));
            }
            // AWAITED: Der Archiv-Move vergibt eine neue Message-Id, die das
            // Backend im eben geschriebenen Link nachzieht. Navigierte der Flow
            // vorher zur Vorgangsseite, holte deren Mail-Tab die Links noch mit
            // der alten Id.
            await archiviereUndEntferne(ctx);
        },
    });
}

/**
 * Das Zuweisen-Popup öffnen und seine beiden Ausgänge bedienen — die eine Naht
 * zu Cluster ZUWEISEN. `standardAbschluss` legt fest, welcher Ausgang
 * vorausgewählt ist: aus „Vorgang erstellen" ohne erkannten Kontakt der Vorgang
 * (E8), aus „Kontakt zuweisen…" die Zuweisung.
 */
function oeffneZuweisung(
    ctx: MailListActionCtx,
    standardAbschluss: "zuweisen" | "vorgang",
    erkennung: MailRecognition,
    // Bewusst eine Funktion und keine fertige Liste: aus „Kontakt zuweisen…"
    // heraus werden die Anhänge erst geholt, WENN der Nutzer im Popup doch auf
    // den Vorgangs-Ausgang geht. Sonst kostete jeder Klick auf „Kontakt
    // zuweisen…" einen Abruf, den der Weg gar nicht braucht.
    holeAnhaenge: () => Promise<Pick<MailListAttachments, "items" | "bilder">>,
): void {
    const snap = buildMailSnapshot(ctx.email, ctx.mailbox);
    openMailContactAssign(ctx.openWindow, {
        senderEmail: ctx.email.senderEmail ?? null,
        senderName: ctx.email.senderName ?? null,
        // B3/B6: Grundlage der MailLink-Verknüpfung — unabhängig davon, ob die
        // Absenderadresse am Kontakt hinterlegt wird.
        mail: snap,
        aktuelleZuweisung: erkennung.assignment,
        standardAbschluss,
        onResolved: async (e: MailZuweisungErgebnis) => {
            if (e.weiter === "vorgang") {
                // Der Flow startet mit dem gewählten Kontakt; bei „geschäftlich"
                // legt er den Vorgang auf die Firma und behält die Person
                // (PO 01.10.2026, `vorgangStartAus`).
                const start = vorgangStartAus(e);
                oeffneVorgangFlow(ctx, start.kontakt, await holeAnhaenge(), start.vorgangFuer);
                return;
            }
            // E03/B5: erst verknüpfen, DANN archivieren. Scheitert das
            // Verknüpfen, wird bewusst NICHT archiviert.
            const r = await schliesseZuweisungAb(e, {
                verknuepfeMailMitKontakt: (contactId) =>
                    mailLinkService.createForContact({ ...snap, contactId }),
                finishRead: () => archiviereUndEntferne(ctx),
            });
            if (r.fehler) ctx.onError(r.fehler);
        },
    });
}

/** Degradierte Erkennung / fehlgeschlagene Anhänge benennen, statt sie zu verschlucken. */
function melde(ctx: MailListActionCtx, erkennung: MailRecognition, anhaengeFehler: boolean): void {
    if (erkennung.degraded) {
        ctx.onNote("Absender-Erkennung nicht verfügbar — bitte Kontakt wählen.");
    }
    if (anhaengeFehler) {
        ctx.onNote("Anhänge konnten nicht geladen werden — der Vorgang wird ohne Anhänge angelegt.");
    }
}

/**
 * Schadenpostfach — „Neuer Schaden" / „Einem bestehenden Schaden zuordnen" aus
 * der Liste. Der erkannte Kontakt ist vorbelegt; nach der Ablage wird die Mail
 * archiviert und verschwindet aus der Liste, wie beim Zuweisen an einen Vorgang.
 */
async function starteSchadenAblage(ctx: MailListActionCtx, modus: SchadenAblageModus): Promise<void> {
    const erkennung = await resolveMailRecognition(ctx.mailbox, ctx.email);
    openSchadenAblage(ctx.openWindow, {
        modus,
        mailbox: ctx.mailbox,
        messageId: ctx.email.id,
        betreff: ctx.email.subject ?? null,
        kontakt: vorgangsKontaktAusErkennung(erkennung),
        onAbgelegt: ({ contactId, ordner, hinweis }) => {
            if (hinweis) ctx.onNote(hinweis);
            void graphMailService
                .archiveMessage(ctx.mailbox, ctx.email.id, { contactId })
                .then(() => {
                    ctx.onDone(ctx.email.id);
                    ctx.onRefreshFolders(ctx.mailbox);
                    // Danach gleich dorthin, wo die Mail jetzt liegt. Scheitert
                    // das Archivieren, bleibt die Liste stehen — sonst wäre die
                    // Fehlermeldung mit dem Seitenwechsel verschwunden.
                    ctx.navigateToSchadenOrdner?.(contactId, ordner);
                })
                .catch((err: unknown) =>
                    ctx.onError(
                        extractMailServerCode(err) === "MAIL_CAPTURE_PENDING"
                            ? `Die Mail liegt im Schaden-Ordner. ${MAIL_CAPTURE_PENDING_MESSAGE}`
                            : "Die Mail liegt im Schaden-Ordner, konnte aber nicht archiviert werden.",
                    ),
                );
        },
    });
}

/**
 * T-180 (28.10.2026) — „Einem Schaden zuordnen…" aus der Liste eines BELIEBIGEN
 * Postfachs: derselbe Weg wie in schaden@ (Ordner der Kundenakte, danach
 * archivieren), nur als eigener Eintrag neben den Vorgangszeilen.
 */
export function startSchadenZuordnen(ctx: MailListActionCtx): Promise<void> {
    return starteSchadenAblage(ctx, "zuordnen");
}

/**
 * G1 — „Vorgang erstellen" aus der Liste.
 *
 * Mit erkanntem Kontakt geht es direkt in den Erstell-Flow; ohne Kontakt zuerst
 * durch das Zuweisen-Popup (E8), dort mit dem Vorgang als vorausgewähltem
 * Ausgang. Beides identisch zum Weg aus der geöffneten Mail.
 */
export async function startVorgangErstellen(ctx: MailListActionCtx): Promise<void> {
    if (ctx.schadenAblage) return starteSchadenAblage(ctx, "neu");
    const [erkennung, anhaenge] = await Promise.all([
        resolveMailRecognition(ctx.mailbox, ctx.email),
        buildMailAttachments(ctx.mailbox, ctx.email),
    ]);
    melde(ctx, erkennung, anhaenge.failed);

    if (erkennung.assignment) {
        // Ein erkannter Ansprechpartner trägt den Vorgang selbst, nicht seine
        // Firma — konsistent zum Weg aus der geöffneten Mail (#1829).
        oeffneVorgangFlow(ctx, vorgangsKontaktAusErkennung(erkennung), anhaenge);
        return;
    }
    oeffneZuweisung(ctx, "vorgang", erkennung, async () => anhaenge);
}

/**
 * G2 — „Einem Vorgang zuweisen…" aus der Liste.
 *
 * Derselbe Picker wie aus der geöffneten Mail. Der erkannte Kontakt reist als
 * `contactId` mit; er bestimmt im Picker die erste Rangstufe („Vorgänge dieses
 * Kontakts", Cluster VORGANG D1). Ohne Kontakt beginnt die Liste bei Stufe 2,
 * ohne Fehlermeldung.
 */
export async function startVorgangZuweisen(ctx: MailListActionCtx): Promise<void> {
    if (ctx.schadenAblage) return starteSchadenAblage(ctx, "zuordnen");
    // T-236: Anhänge schon jetzt holen — nach der Wahl im Picker wird nach den
    // Bildern aus dem Mailtext gefragt. Die Datei-Anhänge wandern ohnehin von
    // selbst mit (Mail-Ablage); ein Fehlschlag kostet hier nur die Frage.
    const anhaengeLaden = buildMailAttachments(ctx.mailbox, ctx.email);
    const erkennung = await resolveMailRecognition(ctx.mailbox, ctx.email);
    melde(ctx, erkennung, false);
    const snap = buildMailSnapshot(ctx.email, ctx.mailbox);

    openMailVorgangPicker(ctx.openWindow, {
        contactId: erkennung.assignment?.id ?? null,
        // T-34: per Knopf als Betreff eines neuen To-dos übernehmbar.
        mailBetreff: ctx.email.subject ?? null,
        onAssign: async (ziele, navigationVorgangId) => {
            const anhaenge = await anhaengeLaden;
            // Gefragt wird VOR dem Zuordnen; übernommen nach dem Schreiben der
            // Links und vor dem Archivieren (neue messageId danach). T-287: mit
            // Vorschau und Einzelauswahl.
            const bilder = await frageBilderAuswahl(
                anhaenge.bilder ?? [],
                anhaenge.items.length,
                "zuordnen",
                (b) => graphMailService.fetchAttachmentBlob(ctx.mailbox, b.messageId, b.id),
                (b) => bildDateiname(b.name, b.contentType, mailDatum(ctx)),
                { groesse: (b) => b.size },
            );
            const mitBildern = bilder.length > 0;
            await assignMailToZiele(ziele, {
                vorArchivieren: mitBildern
                    ? async (gebunden) => {
                          try {
                              await uebernimmBilderInZiele({
                                  mailbox: ctx.mailbox,
                                  bilder,
                                  datum: mailDatum(ctx),
                                  ziele: gebunden,
                              });
                          } catch (err) {
                              ctx.onError(reportMailError({
                                  area: "mail",
                                  action: "save-attachment",
                                  error: err,
                                  fallbackMessage:
                                      "Die Bilder aus dem Mailtext konnten nicht in den Vorgang übernommen werden.",
                              }));
                          }
                      }
                    : undefined,
                // Seit 09/2026 wandert der ganze Verlauf mit — derselbe Weg wie
                // aus der geöffneten Mail (`mailVerlaufBinden`).
                bindVerlauf: (target) =>
                    bindeVerlaufAnZiel({
                        mailbox: ctx.mailbox,
                        offen: snap,
                        conversationId: ctx.email.conversationId ?? null,
                        ziel: target,
                        istImPosteingang: ctx.istImPosteingang ?? (() => false),
                        mitgenommeneNennen: ctx.mitgenommeneNennen,
                    }),
                // Garantiert die Reihenfolge „erst alle Links, dann archivieren":
                // ein Archivieren mitten in der Schleife ließe die restlichen
                // Links auf die alte Message-Id zeigen.
                finishRead: () => archiviereUndEntferne(ctx),
                melde: ctx.onNote,
            });
            if (navigationVorgangId != null) {
                ctx.navigateToVorgang(navigationVorgangId);
            }
        },
    });
}

/**
 * G3 — „Kontakt zuweisen…" aus der Liste.
 *
 * Das Popup hinterlegt die Adresse am gewählten Kontakt und liefert das
 * Ergebnis; verknüpft und archiviert wird danach hier (E03). Wählt der Nutzer
 * „Ohne Kontakt", passiert nichts weiter — die Mail bleibt liegen.
 */
export async function startKontaktZuweisen(ctx: MailListActionCtx): Promise<void> {
    const erkennung = await resolveMailRecognition(ctx.mailbox, ctx.email);
    melde(ctx, erkennung, false);
    // Anhänge werden hier NICHT vorab geladen — dieser Weg legt keinen Vorgang
    // an. Erst wenn der Nutzer im Popup auf den Vorgangs-Ausgang wechselt,
    // werden sie nachgeholt, damit die Übernahme-Liste dieselbe ist wie überall.
    oeffneZuweisung(ctx, "zuweisen", erkennung, async () => {
        const a = await buildMailAttachments(ctx.mailbox, ctx.email);
        if (a.failed) {
            ctx.onNote(
                "Anhänge konnten nicht geladen werden — der Vorgang wird ohne Anhänge angelegt.",
            );
        }
        return a;
    });
}
